import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import usePageMeta from "../lib/usePageMeta";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageMeta({
    title: post ? `${post.title} | American Asset Recovery Agency` : "Loading…",
    description: post?.meta_description || post?.excerpt || "Surplus foreclosure funds insights from American Asset Recovery Agency.",
    canonical: post ? `https://americanassetrecoveryagency.com/blog/${post.slug}` : undefined,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Header />
        <div className="pt-40 text-center text-[#64748B]">Loading…</div>
      </div>
    );
  }

  if (error === "not_found" || !post) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Header />
        <main className="pt-40 pb-20 max-w-2xl mx-auto px-6 text-center">
          <h1 className="font-serif-display text-3xl text-[#0F172A]">Article not found</h1>
          <p className="mt-3 text-[#64748B]">The article you're looking for may have been moved or unpublished.</p>
          <Link to="/blog" className="inline-block mt-6 text-[#1E3A8A] hover:text-[#FF008F] font-medium">← Back to all articles</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="blog-post">
      <Header />

      <main className="pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center text-sm text-[#475569] hover:text-[#FF008F] mb-6" data-testid="back-to-blog">
              <ArrowLeft className="w-4 h-4 mr-2" /> All articles
            </Link>

            {post.tags?.[0] && (
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">{post.tags[0]}</p>
            )}
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-[1.05]">{post.title}</h1>
            <p className="mt-6 text-lg text-[#475569] leading-relaxed">{post.excerpt}</p>

            <div className="mt-6 flex items-center gap-3 text-sm text-[#64748B]">
              <span>{post.author_name || "American Asset Recovery Agency"}</span>
              {post.published_at && (
                <>
                  <span>·</span>
                  <span>{new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                </>
              )}
            </div>
          </Reveal>

          {post.cover_image_url && (
            <Reveal delay={0.1}>
              <div className="mt-10 relative aspect-[16/9] rounded-2xl overflow-hidden border border-[#E2DDD3] shadow-xl shadow-[#0F172A]/10">
                <img src={post.cover_image_url} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <div className="prose prose-aara mt-12">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>
          </Reveal>

          {/* CTA card */}
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-3xl bg-[#0F172A] text-[#F7F5F0] p-8 sm:p-10 grain-bg overflow-hidden relative">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF008F] mb-3">Free claim check</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight max-w-xl">Wondering if surplus funds are waiting for you?</h3>
              <p className="mt-3 text-[#F7F5F0]/80 max-w-xl">Tell us about the foreclosed property and we'll check the county records — at no cost to you.</p>
              <button
                onClick={() => navigate("/#claim")}
                className="mt-6 inline-flex items-center bg-[#FF008F] hover:bg-[#D60079] text-white font-bold rounded-full px-7 h-12 text-base transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-[#FF008F]/30"
                data-testid="post-cta-claim"
              >
                Check my free claim <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </Reveal>
        </article>
      </main>

      <Footer />
    </div>
  );
}
